import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { CatProduct } from '@/mocks/categoryProducts';
import ProductReviews from './ProductReviews';

interface ColorVariant {
  name: string;
  hex: string;
  image?: string;
  images?: string[];
  price?: number;
}

function getSizeStock(
  stockBreakdown: Record<string, unknown> | undefined,
  colors: ColorVariant[] | undefined,
  sizes: string[],
  colorName: string | undefined,
  size: string
): number {
  if (!stockBreakdown) return Infinity;
  const hasColors = (colors?.length ?? 0) > 0;
  const hasSizes = sizes.length > 0;
  if (hasColors && hasSizes) {
    if (colorName) {
      const colorMap = stockBreakdown[colorName] as Record<string, number> | undefined;
      return typeof colorMap?.[size] === 'number' ? colorMap[size] : 0;
    }
    let total = 0;
    (colors ?? []).forEach(cv => {
      const colorMap = stockBreakdown[cv.name] as Record<string, number> | undefined;
      total += typeof colorMap?.[size] === 'number' ? colorMap[size] : 0;
    });
    return total;
  }
  if (!hasColors && hasSizes) return typeof stockBreakdown[size] === 'number' ? (stockBreakdown[size] as number) : 0;
  if (hasColors && !hasSizes) {
    if (colorName) return typeof stockBreakdown[colorName] === 'number' ? (stockBreakdown[colorName] as number) : 0;
    return 0;
  }
  return typeof stockBreakdown['total'] === 'number' ? (stockBreakdown['total'] as number) : 0;
}

// Similar Products Section Component
interface SimilarProductsSectionProps {
  allProducts: CatProduct[];
  currentProductId?: number;
  onProductClick?: (product: CatProduct) => void;
}

function SimilarProductsSection({ allProducts, currentProductId, onProductClick }: SimilarProductsSectionProps) {
  const suggested = useMemo(() => {
    if (!currentProductId) return [];
    const current = allProducts.find(p => p.id === currentProductId);
    if (!current) return [];
    return allProducts
      .filter(p => p.id !== currentProductId && !p.outOfStock && p.category === current.category)
      .slice(0, 10);
  }, [allProducts, currentProductId]);

  if (suggested.length === 0) return null;

  return (
    <div className="mt-5 pt-4 border-t border-gray-100">
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1 h-5 rounded-full bg-orange-400"></div>
        <h4 className="text-gray-900 font-bold text-sm">Similar Products</h4>
        <span className="text-gray-400 text-xs ml-auto">{suggested.length} items</span>
      </div>

      {/* Horizontal scroll cards */}
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {suggested.map((product) => {
          const discount = Math.round((1 - product.price / product.originalPrice) * 100);
          return (
            <div
              key={product.id}
              className="shrink-0 flex flex-col rounded-2xl bg-white border border-gray-100 overflow-hidden cursor-pointer"
              style={{ width: '130px' }}
              onClick={() => onProductClick?.(product)}
            >
              {/* Product Image */}
              <div className="relative bg-gray-50" style={{ height: '160px' }}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                {discount > 0 && (
                  <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    {discount}% OFF
                  </span>
                )}
                {product.badge && (
                  <span className={`absolute top-2 right-2 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                    product.badge === 'NEW' ? 'bg-green-500' :
                    product.badge === 'BESTSELLER' ? 'bg-amber-500' :
                    product.badge === 'TRENDING' ? 'bg-red-400' :
                    'bg-gray-700'
                  }`}>
                    {product.badge}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="p-2.5 flex flex-col flex-1">
                <p className="text-gray-900 font-semibold text-xs leading-tight mb-1.5 line-clamp-2">
                  {product.name}
                </p>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-gray-900 font-bold text-sm">₹{product.price}</span>
                  {product.originalPrice > product.price && (
                    <span className="text-gray-400 line-through text-[10px]">₹{product.originalPrice}</span>
                  )}
                </div>

                {/* Size chips */}
                {product.sizes.length > 0 && (
                  <div className="flex gap-0.5 mb-2.5 flex-wrap">
                    {product.sizes.slice(0, 3).map((size) => (
                      <span
                        key={size}
                        className="w-5 h-5 rounded text-[8px] font-semibold bg-gray-100 text-gray-500 flex items-center justify-center"
                      >
                        {size}
                      </span>
                    ))}
                    {product.sizes.length > 3 && (
                      <span className="w-5 h-5 rounded text-[8px] text-gray-400 bg-gray-100 flex items-center justify-center">
                        +{product.sizes.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col gap-1 mt-auto">
                  <button
                    onClick={(e) => { e.stopPropagation(); }}
                    className="w-full py-2 rounded-xl bg-gray-900 text-white font-bold text-[10px] flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-flashlight-fill text-[9px]"></i>
                    Order Now
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); }}
                    className="w-full py-1.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[10px] flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-bag-line text-[9px]"></i>
                    Add Cart
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ImageGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  productName: string;
  price: number;
  originalPrice: number;
  colors?: ColorVariant[];
  defaultSelectedColor?: ColorVariant | null;
  sizes?: string[];
  description?: string;
  stockBreakdown?: Record<string, unknown>;
  video?: string; // 5-second product video URL
  videoThumbnail?: string; // Video poster image
  onOrder?: (selectedColor: ColorVariant | null, selectedSize: string) => void;
  onAddToCart?: (selectedColor: ColorVariant | null, selectedSize: string) => void;
  // Similar products
  allProducts?: CatProduct[];
  currentProductId?: number;
  onSimilarProductClick?: (product: CatProduct) => void;
}

export default function ImageGalleryModal({
  isOpen,
  onClose,
  images,
  productName,
  price,
  originalPrice,
  colors,
  defaultSelectedColor,
  sizes = ['S', 'M', 'L', 'XL'],
  description,
  stockBreakdown,
  video,
  videoThumbnail,
  onOrder,
  onAddToCart,
  allProducts = [],
  currentProductId,
  onSimilarProductClick,
}: ImageGalleryModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState<ColorVariant | null>(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const currentImages =
    selectedColor?.images && selectedColor.images.length > 0
      ? selectedColor.images
      : selectedColor?.image
      ? [selectedColor.image, ...images.filter((img) => img !== selectedColor.image)]
      : images;

  // Combine images + video for carousel (video first if available)
  const carouselItems = video 
    ? [{ type: 'video' as const, url: video, thumbnail: videoThumbnail }, ...currentImages.map(img => ({ type: 'image' as const, url: img }))]
    : currentImages.map(img => ({ type: 'image' as const, url: img }));

  const currentPrice = selectedColor?.price ?? price;
  const discountPct = Math.round((1 - currentPrice / originalPrice) * 100);

  const getImageFocus = (): string => {
    const name = productName.toLowerCase();
    const lowerKw = ['pant', 'trouser', 'track pant', 'jogger', 'shorts', 'jeans', 'legging', 'capri', 'bottom', 'lower'];
    if (lowerKw.some((kw) => name.includes(kw))) return 'object-bottom';
    const upperKw = ['shirt', 't-shirt', 'tshirt', 'tee', 'top', 'hoodie', 'jacket', 'sweatshirt', 'polo', 'blouse', 'kurta', 'sweater'];
    if (upperKw.some((kw) => name.includes(kw))) return 'object-top';
    return 'object-center';
  };
  const imageFocus = getImageFocus();

  const showToastMsg = (message: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(message);
    setShowToast(true);
    toastTimeoutRef.current = setTimeout(() => setShowToast(false), 2500);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setActiveIndex(0);
      setSelectedColor(defaultSelectedColor ?? (colors && colors.length > 0 ? colors[0] : null));
      setSelectedSize('');
      setZoomIndex(null);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
    if (scrollRef.current) scrollRef.current.scrollTo({ left: 0, behavior: 'instant' });
  }, [selectedColor]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const newIndex = Math.round(scrollRef.current.scrollLeft / scrollRef.current.offsetWidth);
      setActiveIndex(Math.min(newIndex, currentImages.length - 1));
    }
  };

  const scrollToImage = (index: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: index * scrollRef.current.offsetWidth, behavior: 'smooth' });
      setActiveIndex(index);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e: React.TouchEvent) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && activeIndex < currentImages.length - 1) scrollToImage(activeIndex + 1);
      else if (diff < 0 && activeIndex > 0) scrollToImage(activeIndex - 1);
    }
  };

  // Keyboard nav
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') { if (zoomIndex !== null) setZoomIndex(null); else onClose(); }
      if (zoomIndex !== null) {
        if (e.key === 'ArrowRight' && zoomIndex < currentImages.length - 1) setZoomIndex(zoomIndex + 1);
        if (e.key === 'ArrowLeft' && zoomIndex > 0) setZoomIndex(zoomIndex - 1);
      } else {
        if (e.key === 'ArrowRight' && activeIndex < currentImages.length - 1) scrollToImage(activeIndex + 1);
        if (e.key === 'ArrowLeft' && activeIndex > 0) scrollToImage(activeIndex - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIndex, zoomIndex, currentImages.length, onClose]);

  // Zoom state
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const lastPinchDist = useRef<number | null>(null);
  const lastTapTime = useRef(0);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const offsetStart = useRef({ x: 0, y: 0 });
  const zoomImgRef = useRef<HTMLDivElement>(null);
  const zoomTouchStartX = useRef(0);
  const zoomTouchEndX = useRef(0);

  useEffect(() => { setZoomScale(1); setZoomOffset({ x: 0, y: 0 }); }, [zoomIndex]);

  const clampOffset = useCallback((scale: number, ox: number, oy: number) => {
    const el = zoomImgRef.current;
    if (!el) return { x: ox, y: oy };
    const maxX = Math.max(0, (el.offsetWidth * (scale - 1)) / 2);
    const maxY = Math.max(0, (el.offsetHeight * (scale - 1)) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, ox)), y: Math.min(maxY, Math.max(-maxY, oy)) };
  }, []);

  const handleZoomTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist.current = Math.sqrt(dx * dx + dy * dy);
      isDragging.current = false;
    } else if (e.touches.length === 1) {
      zoomTouchStartX.current = e.touches[0].clientX;
      const now = Date.now();
      if (now - lastTapTime.current < 300) {
        if (zoomScale > 1) { setZoomScale(1); setZoomOffset({ x: 0, y: 0 }); }
        else { setZoomScale(2.5); setZoomOffset({ x: 0, y: 0 }); }
        lastTapTime.current = 0;
      } else { lastTapTime.current = now; }
      if (zoomScale > 1) {
        isDragging.current = true;
        dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        offsetStart.current = { ...zoomOffset };
      }
    }
  };

  const handleZoomTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (lastPinchDist.current !== null) {
        const delta = dist / lastPinchDist.current;
        setZoomScale((prev) => Math.min(5, Math.max(1, prev * delta)));
      }
      lastPinchDist.current = dist;
      isDragging.current = false;
    } else if (e.touches.length === 1) {
      zoomTouchEndX.current = e.touches[0].clientX;
      if (isDragging.current && zoomScale > 1) {
        const dx = e.touches[0].clientX - dragStart.current.x;
        const dy = e.touches[0].clientY - dragStart.current.y;
        setZoomOffset(clampOffset(zoomScale, offsetStart.current.x + dx, offsetStart.current.y + dy));
      }
    }
  };

  const handleZoomTouchEnd = (e: React.TouchEvent) => {
    lastPinchDist.current = null;
    if (e.touches.length === 0 && !isDragging.current) {
      const diff = zoomTouchStartX.current - zoomTouchEndX.current;
      if (Math.abs(diff) > 60 && zoomScale <= 1 && zoomIndex !== null) {
        if (diff > 0 && zoomIndex < currentImages.length - 1) setZoomIndex(zoomIndex + 1);
        else if (diff < 0 && zoomIndex > 0) setZoomIndex(zoomIndex - 1);
      }
    }
    if (e.touches.length === 0) isDragging.current = false;
  };

  const handleOrderClick = () => {
    if (!selectedSize) {
      showToastMsg('Pehle size select karo!');
      return;
    }
    onOrder?.(selectedColor, selectedSize);
    onClose();
  };

  const handleAddToCartClick = () => {
    if (!selectedSize) {
      showToastMsg('Pehle size select karo!');
      return;
    }
    onAddToCart?.(selectedColor, selectedSize);
    showToastMsg(`${productName}${selectedColor ? ` (${selectedColor.name})` : ''} (${selectedSize}) cart mein add ho gaya!`);
    setTimeout(() => onClose(), 800);
  };

  const renderSizeButton = (size: string) => {
    const sv = getSizeStock(stockBreakdown, colors, sizes, selectedColor?.name, size);
    const isSizeOOS = sv === 0;
    const isSizeLow = sv === 1;
    const isSelected = selectedSize === size;
    return (
      <div key={size} className="relative">
        <button
          onClick={() => !isSizeOOS && setSelectedSize(size)}
          disabled={isSizeOOS}
          className={`min-w-[52px] h-11 px-3 rounded-xl font-bold text-sm transition-all relative border-2 ${
            isSizeOOS
              ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
              : isSelected
                ? 'bg-gray-900 text-white border-gray-900 scale-105'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400 cursor-pointer'
          }`}
        >
          {size}
          {isSizeOOS && (
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-xl pointer-events-none">
              <div className="w-full h-0.5 bg-gray-200 absolute rotate-45"></div>
            </div>
          )}
        </button>
        {isSizeLow && !isSizeOOS && (
          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-white text-[8px] font-bold">1</span>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Toast */}
      {showToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[150] animate-slide-in pointer-events-none">
          <div className="bg-gray-900 text-white px-5 py-3 rounded-full flex items-center gap-2 shadow-xl">
            <i className="ri-check-line text-green-400 text-lg"></i>
            <span className="text-sm font-semibold whitespace-nowrap">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

        {/* Modal Sheet - Full scrollable content */}
        <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl h-[96vh] sm:h-[92vh] shadow-2xl overflow-hidden flex flex-col">
          
          {/* Main scrollable area */}
          <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
            
            {/* Image/Video Carousel */}
            <div className="relative bg-white h-[65vh] sm:h-[70vh]" style={{ maxHeight: '580px', minHeight: '380px' }}>
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className="w-full h-full overflow-x-auto snap-x snap-mandatory flex"
                style={{ scrollBehavior: 'smooth', scrollbarWidth: 'none' }}
              >
                {carouselItems.map((item, index) => (
                  <div
                    key={index}
                    className="h-full w-full flex-shrink-0 snap-center relative overflow-hidden cursor-zoom-in"
                    onClick={() => item.type === 'image' && setZoomIndex(index)}
                  >
                    {item.type === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center bg-black">
                        <video
                          src={item.url}
                          poster={item.thumbnail}
                          className="w-full h-full object-contain"
                          controls
                          playsInline
                          muted
                          loop
                          preload="metadata"
                        />
                        {/* Video badge */}
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-purple-500 text-white text-xs font-bold flex items-center gap-1">
                          <i className="ri-video-line"></i>
                          5-sec Video
                        </div>
                      </div>
                    ) : (
                      <img
                        src={item.url}
                        alt={`${productName} ${index + 1}`}
                        className="w-full h-full object-contain select-none"
                        draggable={false}
                        loading={index <= 2 ? 'eager' : 'lazy'}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white cursor-pointer z-10"
              >
                <i className="ri-close-line text-lg"></i>
              </button>

              {/* Image counter pill */}
              {currentImages.length > 1 && (
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white text-xs font-semibold z-10">
                  {activeIndex + 1} / {currentImages.length}
                </div>
              )}

              {/* Dot indicators */}
              {currentImages.length > 1 && (
                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10">
                  {currentImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => scrollToImage(i)}
                      className={`rounded-full transition-all cursor-pointer ${
                        i === activeIndex ? 'w-4 h-2 bg-white' : 'w-2 h-2 bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Discount badge */}
              {discountPct > 0 && (
                <div className="absolute bottom-3 right-3 z-10">
                  <span className="px-2.5 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
                    {discountPct}% OFF
                  </span>
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {carouselItems.length > 1 && (
              <div className="px-4 pt-2 sm:pt-3">
                <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                  {carouselItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => scrollToImage(index)}
                      className={`flex-shrink-0 w-12 sm:w-14 rounded-xl overflow-hidden border-2 transition-all cursor-pointer relative ${
                        activeIndex === index ? 'border-gray-900' : 'border-gray-200'
                      }`}
                      style={{ height: '64px' }}
                    >
                      {item.type === 'video' ? (
                        <>
                          <img
                            src={item.thumbnail || item.url}
                            alt="Video thumbnail"
                            className={`w-full h-full object-cover ${imageFocus}`}
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <i className="ri-play-circle-fill text-white text-lg"></i>
                          </div>
                        </>
                      ) : (
                        <img
                          src={item.url}
                          alt={`thumb ${index + 1}`}
                          className={`w-full h-full object-cover ${imageFocus}`}
                          loading="lazy"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Product Details */}
            <div className="px-4 pt-3 pb-24">
              
              {/* Product Name + Price */}
              <div className="mb-3">
                <h3 className="text-gray-900 font-bold text-base leading-tight mb-1">{productName}</h3>
                {description && (
                  <p className="text-gray-400 text-xs leading-relaxed mb-2">{description}</p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-900 font-bold text-2xl">₹{currentPrice}</span>
                  {originalPrice > currentPrice && (
                    <span className="text-gray-400 line-through text-sm">₹{originalPrice}</span>
                  )}
                  {discountPct > 0 && (
                    <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-0.5 rounded-full">
                      Save ₹{originalPrice - currentPrice}
                    </span>
                  )}
                </div>
              </div>

              {/* Color Selection */}
              {colors && colors.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-sm font-bold text-gray-800">Colour</span>
                    {selectedColor && (
                      <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">
                        {selectedColor.name}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    {colors.map((color) => {
                      const isSelected = selectedColor?.name === color.name;
                      const isLight = ['#FFFFFF', '#ffffff', '#fff', '#f5f5f5', '#fafafa', '#eeeeee', '#e5e7eb'].includes(color.hex);
                      return (
                        <button
                          key={color.name}
                          onClick={() => setSelectedColor(color)}
                          title={color.name}
                          className="relative flex flex-col items-center gap-1 cursor-pointer group"
                        >
                          <div
                            className={`w-10 h-10 rounded-full transition-all flex items-center justify-center ${
                              isSelected
                                ? 'ring-2 ring-offset-2 ring-gray-900 scale-110'
                                : 'ring-1 ring-gray-200 hover:ring-gray-400'
                            }`}
                            style={{ backgroundColor: color.hex }}
                          >
                            {isSelected && (
                              <i className={`ri-check-line text-sm font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}></i>
                            )}
                          </div>
                          <span className={`text-[10px] font-medium leading-none max-w-[44px] truncate text-center ${isSelected ? 'text-gray-900' : 'text-gray-400'}`}>
                            {color.name}
                          </span>
                          {color.price && color.price !== price && (
                            <span className={`text-[9px] font-bold px-1 rounded-full ${
                              color.price > price ? 'text-red-500 bg-red-50' : 'text-green-600 bg-green-50'
                            }`}>
                              {color.price > price ? `+₹${color.price - price}` : `-₹${price - color.price}`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Size Selection */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800">Size</span>
                    {selectedSize && (
                      <span className="text-xs font-semibold text-white bg-gray-900 px-2.5 py-0.5 rounded-full">
                        {selectedSize}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-green-600 flex items-center gap-1 font-medium">
                    <i className="ri-exchange-line"></i>Easy Exchange
                  </span>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {sizes.map((size) => renderSizeButton(size))}
                </div>

                {stockBreakdown && sizes.some((sz) =>
                  getSizeStock(stockBreakdown, colors, sizes, selectedColor?.name, sz) === 0
                ) && (
                  <div className="flex items-center gap-1.5 mt-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                    <i className="ri-close-circle-line text-red-400 text-sm shrink-0"></i>
                    <p className="text-red-500 text-xs">Strikethrough sizes are out of stock</p>
                  </div>
                )}

                {!selectedSize && (
                  <p className="text-gray-400 text-xs mt-2 flex items-center gap-1">
                    <i className="ri-information-line"></i>
                    Size select karo phir order ya cart mein add karo
                  </p>
                )}
              </div>

              {/* Delivery info strip */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5 mb-4">
                <div className="w-7 h-7 flex items-center justify-center shrink-0">
                  <i className="ri-truck-line text-gray-600 text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-800 text-xs font-semibold">Free Delivery · Cash on Delivery</p>
                  <p className="text-gray-400 text-[10px]">Pay when your order arrives at door</p>
                </div>
                <span className="text-green-600 font-bold text-xs whitespace-nowrap">FREE</span>
              </div>

              {/* Similar Products */}
              <SimilarProductsSection
                allProducts={allProducts}
                currentProductId={currentProductId}
                onProductClick={onSimilarProductClick}
              />

              {/* Customer Reviews */}
              <ProductReviews
                productName={productName}
                productCategory={allProducts.find(p => p.id === currentProductId)?.category}
              />
            </div>
          </div>

          {/* Sticky Bottom Action Buttons */}
          <div className="shrink-0 px-4 py-4 border-t border-gray-100 bg-white absolute bottom-0 left-0 right-0">
            <div className="flex gap-2.5">
              <button
                onClick={handleAddToCartClick}
                className={`flex-1 py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer transition-all whitespace-nowrap border-2 ${
                  selectedSize
                    ? 'border-gray-900 text-gray-900 hover:bg-gray-50'
                    : 'border-gray-200 text-gray-400'
                }`}
              >
                <i className="ri-shopping-bag-line text-base"></i>
                Add to Cart
              </button>
              <button
                onClick={handleOrderClick}
                className={`flex-1 py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer transition-all whitespace-nowrap ${
                  selectedSize
                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                <i className="ri-flashlight-fill text-base"></i>
                Order Now
              </button>
            </div>
            {!selectedSize && (
              <p className="text-center text-gray-400 text-xs mt-2">
                <i className="ri-arrow-up-line mr-1"></i>Size select karo — upar se choose karo
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Full Screen Zoom View */}
      {zoomIndex !== null && (
        <div
          className="fixed inset-0 z-[200] bg-black flex flex-col select-none"
          onTouchStart={handleZoomTouchStart}
          onTouchMove={handleZoomTouchMove}
          onTouchEnd={handleZoomTouchEnd}
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0 z-10">
            <div className="px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium">
              {zoomIndex + 1} / {currentImages.length}
            </div>
            {zoomScale > 1 && (
              <div className="px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium">
                {zoomScale.toFixed(1)}×
              </div>
            )}
            <button
              onClick={() => { setZoomIndex(null); setZoomScale(1); setZoomOffset({ x: 0, y: 0 }); }}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div
            ref={zoomImgRef}
            className="flex-1 min-h-0 overflow-hidden flex items-center justify-center"
            style={{ touchAction: zoomScale > 1 ? 'none' : 'pan-y' }}
          >
            <img
              src={currentImages[zoomIndex]}
              alt={`${productName} full view ${zoomIndex + 1}`}
              className="w-full h-full object-contain select-none"
              draggable={false}
              style={{
                transform: `scale(${zoomScale}) translate(${zoomOffset.x / zoomScale}px, ${zoomOffset.y / zoomScale}px)`,
                transition: isDragging.current ? 'none' : 'transform 0.2s ease',
                willChange: 'transform',
              }}
            />
          </div>

          {zoomScale <= 1 && (
            <div className="flex items-center justify-between px-4 pb-6 shrink-0">
              <button
                onClick={() => zoomIndex > 0 && setZoomIndex(zoomIndex - 1)}
                disabled={zoomIndex === 0}
                className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white disabled:opacity-30 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
              <div className="flex gap-1.5">
                {currentImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setZoomIndex(i)}
                    className={`rounded-full transition-all cursor-pointer ${i === zoomIndex ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/40'}`}
                  />
                ))}
              </div>
              <button
                onClick={() => zoomIndex < currentImages.length - 1 && setZoomIndex(zoomIndex + 1)}
                disabled={zoomIndex === currentImages.length - 1}
                className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white disabled:opacity-30 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            </div>
          )}

          {zoomScale > 1 ? (
            <div className="flex justify-center pb-5 shrink-0">
              <button
                onClick={() => { setZoomScale(1); setZoomOffset({ x: 0, y: 0 }); }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-white text-sm cursor-pointer"
              >
                <i className="ri-zoom-out-line"></i>Reset Zoom
              </button>
            </div>
          ) : (
            <p className="text-center text-white/30 text-xs pb-4">
              <i className="ri-zoom-in-line mr-1"></i>Pinch or double-tap to zoom
            </p>
          )}
        </div>
      )}
    </>
  );
}
